import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MarkAvailableModal from './MarkAvailableModal';
import { EXEMPT_SOURCES } from './StageBadge';

// "Add to Pipeline" picker, opened from the Pipeline page's Add Deal button. Lists the logged-in
// agent's own deals that aren't yet surfaced in the shared Pipeline (stage <> 'Available') and lets
// them mark one Available with one click — which makes it appear in the org-wide Active Deals box.
// Creating a brand-new deal from scratch is still available via the footer link.
//
// Publishing a deal is not one click any more: the board is what the whole team sells from, so the
// deal has to be complete first. Picking one opens MarkAvailableModal, which collects the required
// fields (backend/lib/availableRequirements.js) and then makes the stage change itself.
export default function AddToPipelineModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [publish, setPublish] = useState(null);   // the row whose required-fields form is open
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/deals/pipeline-candidates').then(r => r.json())
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Bridge- and import-sourced rows publish straight through; everything else goes via the form.
  async function markAvailable(row) {
    if (!EXEMPT_SOURCES.has(row.deal_source)) { setPublish(row); return; }
    setBusyId(row.id);
    try {
      const r = await fetch(`/api/deals/${row.id}/stage`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stage: 'Available' }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      onPublished(row.id);
    } catch (e) { alert(`Failed to mark available: ${e.message}`); }
    setBusyId(null);
  }

  function onPublished(id) {
    setRows(prev => prev.filter(x => x.id !== id));   // it's Available now — drop from the picker
    if (onChanged) onChanged();
  }

  const filtered = q
    ? rows.filter(r => `${r.address || ''} ${r.city || ''} ${r.zip || ''}`.toLowerCase().includes(q.toLowerCase()))
    : rows;

  // The required-fields form sits above the picker; the picker stays open behind it.
  if (publish) {
    return (
      <MarkAvailableModal
        dealId={publish.id}
        address={publish.address}
        onClose={() => setPublish(null)}
        onPublished={() => { onPublished(publish.id); setPublish(null); }}
      />
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Add to Pipeline</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
              Pick one of your deals to mark <b>Available</b> — it appears in the shared Pipeline for the whole team.
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px 0' }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search your deals by address…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13 }} />
        </div>

        {/* List */}
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading your deals…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
              {rows.length === 0 ? 'All your live deals are already in the Pipeline (or you have none yet).' : 'No deals match your search.'}
            </div>
          ) : (
            filtered.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(232,227,218,0.5)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.address}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[r.city, r.state, r.zip].filter(Boolean).join(', ')} · <span style={{ color: 'var(--gray-300)' }}>{r.stage}</span></div>
                </div>
                <button onClick={() => markAvailable(r)} disabled={busyId === r.id}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(74,124,89,0.4)', background: 'rgba(74,124,89,0.14)', color: '#4A7C59', fontSize: 12, fontWeight: 700, cursor: busyId === r.id ? 'default' : 'pointer', opacity: busyId === r.id ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                  {busyId === r.id ? 'Adding…' : '↑ Mark Available'}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            Need a brand-new deal? <span onClick={() => navigate('/deals/new')} style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>Create one from scratch →</span>
          </span>
          <button onClick={onClose} style={ghostBtn}>Done</button>
        </div>
      </div>
    </div>
  );
}

const overlay  = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel    = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(640px, 100%)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const closeBtn = { background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: 0 };
const ghostBtn = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
